import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import {
  CheckCircle2,
  Download,
  FileText,
  ShieldCheck,
  Clock3,
  Link2,
  X,
  Check,
  Lock,
  Key,
  BadgeCheck,
  Stamp,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const steps = [
  "Upload",
  "Configure Digital Identity",
  "Choose Mode",
  "Advanced Options",
  "Customize Signature",
  "Review & Sign",
];

const modeLabels = {
  pq: "Post-Quantum (PQ) - Dilithium3",
  hybrid: "Hybrid (PQ + RSA)",
  classic: "Classic RSA",
};

const DEFAULT_MODE_LABEL = "Post-Quantum (PQ) - Dilithium3";
const VALUE_CLASS = "text-sm font-semibold text-gray-800";

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */

// First truthy value, otherwise the fallback (same as `a || b || fallback`).
function pick(fallback, ...values) {
  return values.find(Boolean) || fallback;
}

function onOff(enabled, onText = "Enabled") {
  return enabled ? onText : "Disabled";
}

function resolveSuccessData(state, user) {
  const {
    documentId: passedDocumentId,
    identity,
    selectedDigitalId,
    signer,
    mode,
    uploadedFiles = [],
    selectedFile: passedFile,
    signedPdf,
    cid,
    algorithm,
    pageSeals,
    timestampEnabled,
    blockchainEnabled,
    visibleSignature,
    visualSealEnabled = false,
    documentEncryptionEnabled = false,
    encryptSignedDoc,
    encryptionAlgorithm,
    certificateEmbeddingEnabled = true,
    certificateChainOption,
    certificateData,
    signatureOptions,
    signingResult,
  } = state;

  return {
    signedPdf,
    pageSeals,
    timestampEnabled,
    blockchainEnabled,
    visibleSignature,
    certificateEmbeddingEnabled,
    certificateChainOption,
    selectedFile: pick(undefined, passedFile, uploadedFiles[0]),
    signedTime: new Date().toLocaleString(),
    documentId: pick(
      "N/A",
      passedDocumentId,
      signingResult?.document_id,
      cid?.slice(0, 12)
    ),
    signerName: pick("John Doe", signer?.name, identity?.name, user?.name),
    signerEmail: pick(
      "john.doe@company.com",
      signer?.email,
      identity?.email,
      user?.email
    ),
    // The certificate file name falls back to a different placeholder.
    certSignerName: pick("signer", signer?.name, identity?.name, user?.name),
    signingModeLabel: pick(DEFAULT_MODE_LABEL, algorithm, modeLabels[mode]),
    issuer: pick("Quantum CA", selectedDigitalId?.issuer, identity?.issuer),
    validTill: pick("N/A", selectedDigitalId?.valid_to, identity?.valid_to),
    isVisualSealEnabled: Boolean(
      pick(
        false,
        visualSealEnabled,
        signatureOptions?.visualSealEnabled,
        signingResult?.visual_seal_enabled
      )
    ),
    isEncrypted: Boolean(
      pick(false, documentEncryptionEnabled, encryptSignedDoc, signingResult?.encrypted)
    ),
    resolvedEncAlgo: pick(
      "AES-256-GCM",
      encryptionAlgorithm,
      signingResult?.encryption_algorithm
    ),
    // Resolve user certificate data from all available sources
    userCertificate: pick(
      null,
      signingResult?.user_certificate,
      signingResult?.certificate,
      signingResult?.cert,
      certificateData,
      selectedDigitalId?.certificate,
      selectedDigitalId?.certificateData,
      identity?.certificate,
      state.userCertificate
    ),
  };
}

function buildReportRows(info) {
  return [
    { label: "Document", value: info.selectedFile?.name || "document.pdf" },
    { label: "Signer", value: info.signerName },
    { label: "Email", value: info.signerEmail },
    { label: "Algorithm", value: info.signingModeLabel },
    { label: "Signed On", value: info.signedTime },
    { label: "Document ID", value: info.documentId },
    { label: "Timestamp", value: onOff(info.timestampEnabled, "Quantum TSA Enabled") },
    { label: "Blockchain", value: onOff(info.blockchainEnabled, "QuantumChain Verified") },
    { label: "Visible Signature", value: onOff(info.visibleSignature) },
    { label: "Visual Seal", value: onOff(info.isVisualSealEnabled, "Applied to Pages") },
    {
      label: "Document Encryption",
      value: onOff(info.isEncrypted, `Enabled (${info.resolvedEncAlgo})`),
    },
    { label: "Certificate Embedding", value: onOff(info.certificateEmbeddingEnabled) },
    { label: "Certificate Issuer", value: info.issuer },
    { label: "Valid Till", value: info.validTill },
  ];
}

/* -------------------------------------------------------------------------- */
/* Download logic                                                              */
/* -------------------------------------------------------------------------- */

function triggerDirectDownload(content, fileName, mimeType = "application/x-x509-ca-cert") {
  const blob = typeof content === "string" ? new Blob([content], { type: mimeType }) : content;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function readErrorMessage(response) {
  const fallback = "Package download failed";
  try {
    const err = await response.json();
    return err.error || fallback;
  } catch {
    // Response was not JSON
    return fallback;
  }
}

function buildZipName(selectedFile, isEncrypted) {
  const baseName = (selectedFile?.name || "document.pdf").replace(/\.pdf$/i, "");
  return `${isEncrypted ? "Signed_Encrypted_" : "Signed_"}${baseName}.zip`;
}

function scheduleCertificateDownload(userCertificate, signerName) {
  setTimeout(() => {
    const certFileName = `${signerName.toLowerCase().replace(/\s+/g, "_")}_certificate.crt`;
    triggerDirectDownload(userCertificate, certFileName, "application/x-x509-ca-cert");
  }, 400);
}

// Request the signed package from the backend (with visual seal state).
function requestSignedPackage(info) {
  const embed = info.certificateEmbeddingEnabled;
  return fetch(`${API_URL}/download-signed-package`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      signed_pdf: info.signedPdf,
      user_certificate: embed ? info.userCertificate : null,
      certificate_embedding_enabled: embed,
      certificate_chain_option: info.certificateChainOption,
      visual_seal_enabled: info.isVisualSealEnabled,
      page_seals: info.isVisualSealEnabled ? info.pageSeals : null,
    }),
  });
}

async function downloadSignedPackage(info) {
  if (!info.signedPdf) {
    alert("No signed PDF found");
    return;
  }

  const response = await requestSignedPackage(info);
  if (!response.ok) {
    alert(await readErrorMessage(response));
    return;
  }

  const blob = await response.blob();
  triggerDirectDownload(blob, buildZipName(info.selectedFile, info.isEncrypted), "application/zip");

  // Fallback separate certificate download if backend returns PDF only
  const needsSeparateCert =
    info.certificateEmbeddingEnabled && info.userCertificate && blob.type.includes("pdf");
  if (needsSeparateCert) {
    scheduleCertificateDownload(info.userCertificate, info.certSignerName);
  }
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress() {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => (
        <div key={step} className="flex-1 flex flex-col items-center relative">
          {index !== steps.length - 1 && (
            <div className="absolute top-5 left-1/2 w-full h-[2px] bg-green-500" />
          )}
          <div className="z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 bg-green-500 border-green-500 text-white">
            <Check size={14} strokeWidth={3} />
          </div>
          <p className="mt-1 text-sm font-medium text-green-600 text-center hidden sm:block">
            {step}
          </p>
        </div>
      ))}
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      {children}
    </div>
  );
}

function DetailText({ label, value }) {
  return (
    <DetailField label={label}>
      <p className={VALUE_CLASS}>{value}</p>
    </DetailField>
  );
}

function IconDetail({ label, icon: Icon, iconClass, children }) {
  return (
    <DetailField label={label}>
      <div className="flex items-center gap-2">
        <Icon className={`${iconClass} flex-shrink-0`} size={16} />
        {children}
      </div>
    </DetailField>
  );
}

function StatusDetail({ label, icon: Icon, active, activeIconClass, activeTextClass, text }) {
  return (
    <DetailField label={label}>
      <div className="flex items-center gap-1.5">
        <Icon size={14} className={active ? activeIconClass : "text-gray-400"} />
        <p className={`text-sm font-semibold ${active ? activeTextClass : "text-gray-800"}`}>
          {text}
        </p>
      </div>
    </DetailField>
  );
}

function SigningDetails({ info }) {
  return (
    <div className="px-8 py-6">
      <h3 className="text-xl font-semibold text-gray-700 mb-4">Signing Details</h3>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        <IconDetail label="Signed By" icon={ShieldCheck} iconClass="text-blue-600">
          <div>
            <p className={VALUE_CLASS}>{info.signerName}</p>
            <p className="text-xs text-gray-500">{info.signerEmail}</p>
          </div>
        </IconDetail>

        <DetailText label="Signing Mode" value={info.signingModeLabel} />

        <IconDetail label="Signed On" icon={Clock3} iconClass="text-orange-500">
          <p className={VALUE_CLASS}>{info.signedTime}</p>
        </IconDetail>

        <DetailText
          label="Timestamp"
          value={onOff(info.timestampEnabled, "Quantum TSA Enabled")}
        />

        <IconDetail label="Blockchain Anchor" icon={Link2} iconClass="text-purple-500">
          <p className={VALUE_CLASS}>
            {onOff(info.blockchainEnabled, "QuantumChain Verified")}
          </p>
        </IconDetail>

        <DetailText label="Visible Signature" value={onOff(info.visibleSignature)} />

        <StatusDetail
          label="Visual Seal"
          icon={Stamp}
          active={info.isVisualSealEnabled}
          activeIconClass="text-indigo-600"
          activeTextClass="text-indigo-700"
          text={onOff(info.isVisualSealEnabled, "Applied to Pages")}
        />

        <StatusDetail
          label="Document Encryption"
          icon={Key}
          active={info.isEncrypted}
          activeIconClass="text-violet-600"
          activeTextClass="text-violet-700"
          text={onOff(info.isEncrypted, `Enabled (${info.resolvedEncAlgo})`)}
        />

        <StatusDetail
          label="Certificate Embedding"
          icon={BadgeCheck}
          active={info.certificateEmbeddingEnabled}
          activeIconClass="text-emerald-600"
          activeTextClass="text-emerald-700"
          text={onOff(info.certificateEmbeddingEnabled, "Embedded & Downloadable")}
        />

        <DetailText label="Document ID" value={info.documentId} />
      </div>
    </div>
  );
}

function ModalShell({ onClose, widthClass, maxHeight, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${widthClass} flex flex-col`}
        style={{ maxHeight }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function CloseButton({ onClick, size }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
    >
      <X size={size} />
    </button>
  );
}

function EncryptedNotice({ onDownload }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-600 gap-3 text-center">
      <Lock size={44} className="text-violet-600" />
      <p className="text-sm font-bold text-slate-800">Document is Password-Protected</p>
      <p className="text-xs text-slate-500 max-w-md">
        This PDF was encrypted during signing. Download the signed file and open it using your
        configured password.
      </p>
      <button
        type="button"
        onClick={onDownload}
        className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold transition shadow-sm"
      >
        <Download size={14} /> Download to Open
      </button>
    </div>
  );
}

function PdfFrame({ src, title }) {
  return (
    <iframe
      src={src}
      title={title}
      className="w-full rounded bg-white shadow-sm"
      style={{ minHeight: "70vh" }}
    />
  );
}

function DocumentPreview({ signedPdf, isEncrypted, selectedFile, onDownload }) {
  if (signedPdf && isEncrypted) return <EncryptedNotice onDownload={onDownload} />;
  if (signedPdf) {
    return (
      <PdfFrame
        src={`data:application/pdf;base64,${signedPdf}`}
        title="Signed Document Preview"
      />
    );
  }
  if (selectedFile?.url) return <PdfFrame src={selectedFile.url} title="Document Preview" />;

  return (
    <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
      <FileText size={48} />
      <p className="text-sm">No document preview available</p>
    </div>
  );
}

function ViewDocumentModal({ info, onClose, onDownload }) {
  return (
    <ModalShell onClose={onClose} widthClass="max-w-4xl" maxHeight="90vh">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <p className="font-semibold text-gray-800 text-sm truncate">
          {info.selectedFile?.name || "Document"}
        </p>
        <CloseButton onClick={onClose} size={18} />
      </div>
      <div className="flex-1 overflow-auto bg-gray-100 flex justify-center p-4">
        <DocumentPreview
          signedPdf={info.signedPdf}
          isEncrypted={info.isEncrypted}
          selectedFile={info.selectedFile}
          onDownload={onDownload}
        />
      </div>
    </ModalShell>
  );
}

function SignatureReportModal({ info, onClose, onDownload }) {
  return (
    <ModalShell onClose={onClose} widthClass="max-w-sm" maxHeight="75vh">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <p className="font-semibold text-gray-800 text-sm">Signature Report</p>
        <CloseButton onClick={onClose} size={16} />
      </div>
      <div className="px-4 py-3 space-y-2 overflow-auto">
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-2.5">
          <CheckCircle2 className="text-green-500 flex-shrink-0" size={18} />
          <div>
            <p className="text-xs font-semibold text-gray-800">Signature Valid</p>
            <p className="text-xs text-gray-500">Verified successfully.</p>
          </div>
        </div>
        {buildReportRows(info).map(({ label, value }) => (
          <div
            key={label}
            className="flex justify-between items-start border-b pb-1.5 last:border-0"
          >
            <p className="text-xs text-gray-400 w-32 flex-shrink-0">{label}</p>
            <p className="text-xs font-semibold text-gray-800 text-right break-all">{value}</p>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4">
        <button
          type="button"
          onClick={onDownload}
          className="w-full inline-flex items-center justify-center gap-2 h-[44px] px-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold rounded-[10px] transition"
        >
          <Download size={13} /> Download Report
        </button>
      </div>
    </ModalShell>
  );
}

const SECONDARY_BUTTON_CLASS =
  "border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition rounded-xl py-2.5 text-sm font-medium text-gray-700 flex items-center justify-center gap-2";

function SuccessActions({ onDownload, onView, onReport }) {
  return (
    <div className="px-8 pb-8 space-y-3 flex flex-col items-center">
      <button
        type="button"
        onClick={onDownload}
        className="inline-flex items-center gap-2 h-[44px] px-6 bg-[#2563EB] hover:bg-[#1D4ED8] transition text-white font-semibold rounded-[10px] text-sm shadow-sm"
      >
        <Download size={15} /> Download Signed Package
      </button>
      <div className="grid grid-cols-2 gap-3 w-full">
        <button type="button" onClick={onView} className={SECONDARY_BUTTON_CLASS}>
          <FileText size={16} /> View Document
        </button>
        <button type="button" onClick={onReport} className={SECONDARY_BUTTON_CLASS}>
          <ShieldCheck size={16} /> View Signature Report
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function SignSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewOpen, setViewOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const info = resolveSuccessData(location.state || {}, user);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, []);

  const handleDownload = async () => {
    try {
      await downloadSignedPackage(info);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download signed package");
    }
  };

  const handleDone = () =>
    navigate(info.selectedFile?.workflowRequestId ? "/request-status" : "/app");

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress />

      <div className="max-w-2xl mx-auto">
        <div className="border rounded-2xl shadow-sm overflow-hidden bg-white">
          {/* TOP — SUCCESS BANNER */}
          <div className="bg-green-50 border-b px-8 py-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full border-4 border-green-500 flex items-center justify-center mb-4 bg-white">
              <CheckCircle2 size={36} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Document Signed Successfully!</h2>
            <p className="text-base font-normal text-gray-500 mt-1 break-all">
              {info.selectedFile?.name || "document.pdf"}
            </p>
            <p className="text-sm font-normal text-gray-400 mt-1">
              {info.isEncrypted
                ? "The document has been signed, encrypted with your password, and securely processed."
                : "The document has been signed and securely processed."}
            </p>
          </div>

          <SigningDetails info={info} />
          <SuccessActions
            onDownload={handleDownload}
            onView={() => setViewOpen(true)}
            onReport={() => setReportOpen(true)}
          />
        </div>

        {/* FOOTER */}
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleDone}
            className="px-6 py-2.5 bg-white border rounded-xl hover:bg-gray-50 transition font-medium text-gray-700 shadow-sm text-sm"
          >
            Done
          </button>
        </div>
      </div>

      {viewOpen && (
        <ViewDocumentModal
          info={info}
          onClose={() => setViewOpen(false)}
          onDownload={handleDownload}
        />
      )}
      {reportOpen && (
        <SignatureReportModal
          info={info}
          onClose={() => setReportOpen(false)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}